import { useState, useCallback } from 'react';
import DataBreachService from '../../../../services/DataBreach.service';
import { useNavigate } from 'react-router-dom';
import useNotify from '../../../../context/Notification/useNotify';
import { useTranslation } from 'react-i18next';
import { ROUTER } from '../../../../constants/router';
import useQuery from '../../../../util/useQuery';

export default function useDsBreach({ isPreview = false }) {
    const [dsBreachList, setDsBreachList] = useState([]);
    const [dsBreachDetail, setDsBreachDetail] = useState();
    const [dsBreachTemplate, setDsBreachTemplate] = useState([]);
    const [loading, setLoading] = useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const notify = useNotify();

    const params = useQuery();
    const groupId = params.get('id');

    const getDataBreachList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await DataBreachService.getDataBreachList();
            if (data.resultData) {
                const resultData = data.resultData;
                setDsBreachList(resultData);
                setLoading(false);
                const formId = resultData[0]?.dataBreachGroupId;
                if (formId && !groupId) {
                    navigate(`/data-breach/form?id=${formId}`);
                }
            } else setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getDataBreachFormById = useCallback(async (id) => {
        setLoading(true);
        try {
            const { data } = await DataBreachService.getDataBreachFormById(id);
            if (data?.resultData) {
                const resultData = data.resultData;
                setDsBreachDetail(resultData);
                setLoading(false);
            } else {
                setDsBreachDetail(null);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
    }, []);

    const getDataBreachFormPublichById = useCallback(
        async (id) => {
            setLoading(true);
            try {
                const { data } =
                    await DataBreachService.getDataBreachFormPublichById(id);
                if (data?.resultData) {
                    const resultData = data.resultData;
                    setDsBreachDetail(resultData);
                    setLoading(false);
                    return resultData;
                } else {
                    if (isPreview) {
                        navigate('/invalid-link');
                    }
                    setDsBreachDetail(null);
                    setLoading(false);
                }
            } catch (error) {
                setLoading(false);
                console.log(error);
            }
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [isPreview]
    );

    const updateDataBreachForm = useCallback(
        async (payload, action) => {
            setLoading(true);
            try {
                const { data } = await DataBreachService.updateDataBreachForm(
                    payload
                );
                if (data?.resultData) {
                    const resultData = data.resultData;
                    setLoading(false);
                    setTimeout(() => {
                        notify({
                            title: t('common.Success'),
                            description: t('Successfully saved data'),
                            timeOut: 1800,
                        });
                        action(resultData.dataBreachGroupId);
                    }, 300);
                    setTimeout(() => {
                        if (payload.isPublish) {
                            navigate(
                                `${ROUTER.DATA_BREACH.FORM}?id=${resultData.dataBreachGroupId}`
                            );
                        } else {
                            navigate(
                                ROUTER.DATA_BREACH.FORM +
                                    '/edit?id=' +
                                    resultData.dataBreachGroupId
                            );
                        }
                    }, 500);
                } else {
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('Failed to submit data'),
                    });
                    setLoading(false);
                }
            } catch (error) {
                setLoading(false);
                notify({
                    type: 'error',
                    title: t('common.Fail'),
                    description: t('Failed to submit data'),
                });
                console.log(error);
            }
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [notify, t]
    );

    const getMasterDataBreachTemplate = useCallback(async () => {
        try {
            const { data } =
                await DataBreachService.getMasterDataBreachTemplate();
            if (data?.resultData) {
                const resultData = data.resultData;
                // Sort sequence
                resultData.sort((a, b) => a.sequence - b.sequence);
                setDsBreachTemplate(resultData);
            }
        } catch (error) {
            console.log(error);
        }
    }, []);

    const deleteDataBreachForm = useCallback(
        async (id, action) => {
            setLoading(true);
            try {
                const { data } = await DataBreachService.deleteDataBreachForm(
                    id
                );
                if (data?.resultCode === 1) {
                    notify({
                        title: t('common.Success'),
                        description: t('Successfully deleted data'),
                        timeOut: 1800,
                    });
                    action();
                } else {
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('Failed to delete data'),
                    });
                    setLoading(false);
                }
            } catch (error) {
                setLoading(false);
                notify({
                    type: 'error',
                    title: t('common.Fail'),
                    description: t('Failed to delete data'),
                });
                console.log(error);
            }
        },
        [notify, t]
    );

    const submitDataBreachForm = useCallback(async (payload) => {
        try {
            const { data } = await DataBreachService.submitDataBreachForm(
                payload
            );
            return !!data?.resultData;
        } catch (error) {
            return false;
        }
    }, []);

    return {
        loading,
        dsBreachList,
        dsBreachDetail,
        dsBreachTemplate,
        getDataBreachList,
        getDataBreachFormById,
        updateDataBreachForm,
        getMasterDataBreachTemplate,
        deleteDataBreachForm,
        getDataBreachFormPublichById,
        submitDataBreachForm,
        dataBreachService: DataBreachService,
    };
}
